import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  TrendingDown,
  Target,
  Users,
  CheckCircle,
  XCircle,
  Percent,
  LayoutDashboard
} from 'lucide-react';
import salesDashboardService from '../../../api/crm/analytics/salesDashboardService';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import Button from '../../../components/common/Button/Button';

const SalesDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('last_30_days');
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });

  // State for dashboard data
  const [kpiData, setKpiData] = useState({
    totalLeads: { value: 0, change: 0, trend: 'up' },
    opportunities: { value: 0, change: 0, trend: 'down' },
    wonDeals: { value: 0, change: 0, trend: 'up' },
    lostDeals: { value: 0, change: 0, trend: 'down' },
    winRate: { value: 0, change: 0, trend: 'up' }
  });

  const [funnelData, setFunnelData] = useState([]);
  const [conversionMetrics, setConversionMetrics] = useState({
    leadToOpportunity: { totalLeads: 0, convertedToOpps: 0, conversionRate: 0 }
  });
  const [recentOpportunities, setRecentOpportunities] = useState([]);

  // Sorting state for recent opportunities
  const [sortColumn, setSortColumn] = useState('expectedCloseDate');
  const [sortDirection, setSortDirection] = useState('desc');

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);

      try {
        const isCustom = Boolean(customRange.startDate && customRange.endDate);
        const finalRange = isCustom ? 'custom' : dateRange;
        const result = await salesDashboardService.getSalesDashboard(finalRange, isCustom ? customRange.startDate : '', isCustom ? customRange.endDate : '');

        if (result.success && result.data) {

          // Set KPIs
          if (result.data.kpis) {
            setKpiData(result.data.kpis);
          }

          // Set Funnel Data
          if (result.data.funnelData) {
            setFunnelData(result.data.funnelData);
          }

          // Set Conversion Metrics
          if (result.data.conversionMetrics) {
            setConversionMetrics(result.data.conversionMetrics);
          }

          // Set Recent Opportunities
          if (result.data.recentOpportunities) {
            setRecentOpportunities(result.data.recentOpportunities);
          }
        } else {
          setError(result.error || 'Failed to load dashboard data');
        }
      } catch (err) {
        setError('An error occurred while loading dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dateRange, customRange.startDate, customRange.endDate]);

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted opportunities for the table
  const getSortedOpportunities = () => {
    if (!sortColumn || !recentOpportunities || recentOpportunities.length === 0) return recentOpportunities;

    return [...recentOpportunities].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'opportunity') {
        aValue = (a.name || a.opportunityName || '').toLowerCase();
        bValue = (b.name || b.opportunityName || '').toLowerCase();
      } else if (sortColumn === 'lead') {
        const aLead = a.leadName || (a.leadId ? `${a.leadId.firstName || ''} ${a.leadId.lastName || ''}`.trim() : '') || a.lead?.name || '';
        const bLead = b.leadName || (b.leadId ? `${b.leadId.firstName || ''} ${b.leadId.lastName || ''}`.trim() : '') || b.lead?.name || '';
        aValue = aLead.toLowerCase();
        bValue = bLead.toLowerCase();
      } else if (sortColumn === 'value') {
        aValue = typeof a.value === 'number' ? a.value : (typeof a.amount === 'number' ? a.amount : 0);
        bValue = typeof b.value === 'number' ? b.value : (typeof b.amount === 'number' ? b.amount : 0);
      } else if (sortColumn === 'status') {
        aValue = (a.stage || '').toLowerCase();
        bValue = (b.stage || '').toLowerCase();
      } else if (sortColumn === 'expectedCloseDate') {
        aValue = new Date(a.expectedCloseDate || 0).getTime();
        bValue = new Date(b.expectedCloseDate || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };





  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  // Handle drill down - navigate to appropriate pages
  const handleDrillDown = (type, filters = {}) => {

    switch (type) {
      case 'leads':
        navigate('/lead_dashboard');
        break;
      case 'opportunities':
        navigate('/Opportunity-dashboard');
        break;
      case 'won':
        navigate('/close_won_lost?stage=won');
        break;
      case 'lost':
        navigate('/close_won_lost?stage=lost');
        break;
      case 'funnel':
        // Navigate to lead dashboard for funnel stages
        navigate('/lead_dashboard');
        break;
      case 'opportunity':
        // Navigate to opportunity dashboard (opportunity detail page removed)
        navigate('/Opportunity-dashboard');
        break;
      default:
    }
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="space-y-6">
      {/* KPI Cards Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white rounded-2xl shadow-sm p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-16"></div>
          </div>
        ))}
      </div>

      {/* Funnel Skeleton */}
      <div className="bg-white rounded-2xl shadow-sm p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 font-sans space-y-6">
      {/* Header Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
              <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
                <LayoutDashboard className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Sales Dashboard</h1>
                <p className="text-sm sm:text-base text-slate-500 mt-1">Pipeline, conversion metrics, and opportunity tracking</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 overflow-x-auto p-1 -m-1 pb-1 md:pb-0">
              {(!customRange.startDate && !customRange.endDate) && (
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="input-field min-w-[150px] shadow-sm text-sm"
                  disabled={loading}
                >
                  <option value="all_time">All Time</option>
                  <option value="today">Today</option>
                  <option value="last_7_days">Last 7 Days</option>
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="last_60_days">Last 60 Days</option>
                  <option value="last_90_days">Last 90 Days</option>
                </select>
              )}

              <DateRangeFilter
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={(newRange) => setCustomRange((prev) => ({ ...prev, ...newRange }))}
                allowFutureDates={false}
              />

              {(customRange.startDate || customRange.endDate) && (
                <button
                  disabled={loading}
                  onClick={() => {
                    setCustomRange({ startDate: '', endDate: '' });
                    setDateRange('last_30_days');
                  }}
                  className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium h-8"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl mb-6">
            <p className="font-medium">Error loading dashboard</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && <LoadingSkeleton />}

        {/* KPI Cards */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-gray-400">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Total Leads</span>
                <Users className="h-5 w-5 text-gray-400" />
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-gray-900">{formatNumber(kpiData.totalLeads.value)}</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-brand">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Prospects</span>
                <Target className="h-5 w-5 text-brand" />
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-gray-900">{kpiData.opportunities.value}</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-green-500">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Won Deals</span>
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-green-600">{kpiData.wonDeals.value}</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-red-500">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Lost Deals</span>
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-red-600">{kpiData.lostDeals.value}</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-brand">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Win Rate</span>
                <Percent className="h-5 w-5 text-brand" />
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-brand">{kpiData.winRate.value}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Lead Conversion Funnel - Full Width */}
        {!loading && (
          <div className="mb-6">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Pipeline</h3>
                <button
                  onClick={() => navigate('/lead_dashboard')}
                  className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium h-8"
                  type="button"
                >
                  View Leads
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {funnelData.map((stage, index) => {
                  // Map "New Leads" to "Total Leads" for display
                  const displayStage = stage.stage === 'New Leads' ? 'Total Leads' : stage.stage;
                  return (
                    <div key={stage.stage} className="bg-gray-50 p-5 rounded-2xl border-l-4 border-brand shadow-sm">
                      <div className="mb-4">
                        <div className="mb-3">
                          <span className="text-sm font-semibold text-gray-900 leading-tight">{displayStage}</span>
                        </div>
                        <div className="text-3xl font-bold text-gray-900 mb-1">{stage.count}</div>
                        <div className="text-xs text-gray-500 font-medium">leads</div>
                      </div>
                      <div className="mb-3">
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-brand h-3 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(stage.conversion, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Value</span>
                          <span className="text-xs font-semibold text-gray-900">₹{formatIndianAmount(stage.value || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Conversion</span>
                          <span className="text-xs font-semibold text-brand">{stage.conversion}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Conversion Metrics - Replacing Team Performance */}
        {!loading && (
          <div className="grid grid-cols-1 gap-6 mb-6 max-w-md">
            {/* Lead to Opportunity Conversion */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Lead → Opportunity</h3>
                <button
                  onClick={() => navigate('/lead_dashboard')}
                  className="text-sm text-brand hover:underline font-medium"
                  type="button"
                >
                  View Leads
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Leads</span>
                  <span className="text-lg font-bold text-gray-900">{formatNumber(conversionMetrics.leadToOpportunity.totalLeads)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Converted to Opps</span>
                  <span className="text-lg font-bold text-brand">{conversionMetrics.leadToOpportunity.convertedToOpps}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                  <div
                    className="bg-brand h-3 rounded-full"
                    style={{ width: `${Math.min(conversionMetrics.leadToOpportunity.conversionRate, 100)}%` }}
                  ></div>
                </div>
                <div className="text-center text-sm text-gray-600 mt-2">
                  Conversion Rate: {conversionMetrics.leadToOpportunity.conversionRate.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Opportunities Section */}
        {!loading && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent</h3>
              <button
                onClick={() => navigate('/Opportunity-dashboard')}
                className="text-sm text-brand hover:underline font-medium"
                type="button"
              >
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              {recentOpportunities.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('opportunity')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Opportunity</span>
                          {sortColumn === 'opportunity' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('lead')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Lead Details</span>
                          {sortColumn === 'lead' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('value')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Value</span>
                          {sortColumn === 'value' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {sortColumn === 'status' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('expectedCloseDate')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Closing</span>
                          {sortColumn === 'expectedCloseDate' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getSortedOpportunities().map((opp, index) => {
                      // Get opportunity name and ID
                      const opportunityName = opp.name || opp.opportunityName || '';
                      const opportunityId = opp.opportunityId || opp.id || '';

                      // Get lead name - try multiple sources
                      const leadName = opp.leadName
                        || (opp.leadId ? `${opp.leadId.firstName || ''} ${opp.leadId.lastName || ''}`.trim() : '')
                        || (opp.lead?.firstName && opp.lead?.lastName ? `${opp.lead.firstName} ${opp.lead.lastName}`.trim() : '')
                        || opp.lead?.name
                        || '';

                      // Get company name - try multiple sources
                      const companyName = opp.company
                        || opp.companyName
                        || opp.leadId?.company
                        || opp.lead?.company
                        || opp.customerId?.companyName
                        || opp.customerId?.name
                        || '';

                      // Format stage for display
                      const stageDisplay = opp.stage
                        ? opp.stage.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                        : 'New';

                      // Format expected close date
                      let expectedCloseFormatted = '';
                      let expectedCloseTitle = 'N/A';
                      if (opp.expectedCloseDate) {
                        try {
                          const expectedDate = new Date(opp.expectedCloseDate);
                          if (!isNaN(expectedDate.getTime())) {
                            expectedCloseFormatted = expectedDate.toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            });
                            expectedCloseTitle = expectedCloseFormatted;
                          }
                        } catch (e) {
                          expectedCloseFormatted = '';
                        }
                      }
                      if (!expectedCloseFormatted) {
                        expectedCloseFormatted = 'N/A';
                      }

                      // Get stage color
                      const getStageColor = (stage) => {
                        if (!stage) return 'bg-gray-100 text-gray-700';
                        const stageLower = stage.toLowerCase();
                        if (stageLower === 'closed_won') return 'bg-green-100 text-green-800';
                        if (stageLower === 'closed_lost') return 'bg-red-100 text-red-800';
                        if (stageLower === 'negotiation') return 'bg-orange-100 text-orange-800';
                        if (stageLower === 'proposal_sent') return 'bg-purple-100 text-purple-800';
                        if (stageLower === 'qualified') return 'bg-yellow-100 text-yellow-800';
                        return 'bg-gray-100 text-gray-700';
                      };

                      // Format value
                      const value = typeof opp.value === 'number' ? opp.value : (typeof opp.amount === 'number' ? opp.amount : 0);
                      const valueFormatted = `₹${formatIndianAmount(value)}`;

                      return (
                        <tr
                          key={opp.id || opp._id || index}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 w-1/4">
                            <div className="min-w-0">
                              <div
                                className="text-sm font-medium text-gray-900 truncate capitalize max-w-[200px]"
                                title={opportunityName || 'N/A'}
                              >
                                {opportunityName || 'N/A'}
                              </div>
                              <div
                                className="text-xs text-gray-500 truncate max-w-[200px]"
                                title={`ID: ${opportunityId || 'N/A'}`}
                              >
                                ID: {opportunityId || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 w-1/4">
                            <div className="min-w-0">
                              <div
                                className="text-sm font-medium text-gray-900 truncate capitalize max-w-[200px]"
                                title={leadName || 'N/A'}
                              >
                                {leadName || 'N/A'}
                              </div>
                              <div
                                className="text-sm text-gray-500 truncate first-letter:capitalize max-w-[200px]"
                                title={companyName || 'N/A'}
                              >
                                {companyName || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 w-1/12">
                            <div
                              className="truncate max-w-[100px]"
                              title={valueFormatted}
                            >
                              {valueFormatted}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm w-1/12 whitespace-nowrap">
                            <span
                              className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getStageColor(opp.stage)}`}
                              title={stageDisplay}
                            >
                              {stageDisplay}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 w-1/12">
                            <div
                              className="truncate max-w-[120px]"
                              title={expectedCloseTitle}
                            >
                              {expectedCloseFormatted}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No recent opportunities found</p>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default SalesDashboard;
